import { useTranslation } from 'react-i18next';
/**
 * DamageReportRepairTasks.js
 *
 * Sub-section inside the Project Damage Report dialog that lists tasks
 * tracking the repair effort, with totals (hours, labor cost) and the
 * ability to link an existing task from the same project. Creating a
 * new repair task happens via the regular Tasks tab (we deep-link
 * back here from a query param).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Link2, X, Wrench } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtHours = (n) => {
  if (n == null) return '—';
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ') || '0h';
};
const fmtMoney = (v, cur) => v == null ? '—' : `${Number(v).toFixed(2)} ${cur || 'PLN'}`;
const STATUS_BADGE = {
  pending: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export default function DamageReportRepairTasks({ report, canEdit, onChanged }) {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [allTasks, setAllTasks] = useState([]);
  const [picking, setPicking] = useState('');

  useEffect(() => {
    if (!report?.project_id) return;
    axios.get(`${API}/projects/${report.project_id}/tasks`, auth)
      .then((r) => setAllTasks(r.data || []))
      .catch(() => setAllTasks([]));
  }, [report?.project_id]); // eslint-disable-line

  const linked = report.linked_tasks || [];
  const linkedIds = new Set(linked.map((t) => t.id));
  const linkable = allTasks.filter((t) => !linkedIds.has(t.id));

  const totalHours = linked.reduce((s, t) => s + (t.actual_hours || 0), 0);
  const costByCur = {};
  linked.forEach((t) => {
    const c = t.currency || 'PLN';
    costByCur[c] = (costByCur[c] || 0) + (t.labor_cost || 0);
  });

  const link = async () => {
    if (!picking) return;
    try {
      const r = await axios.post(`${API}/project-damage-reports/${report.id}/link-task`,
        { task_id: picking }, auth);
      toast.success(t('auto.misc2.taskLinked'));
      setPicking('');
      onChanged?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
  };

  const unlink = async (taskId) => {
    try {
      const r = await axios.delete(`${API}/project-damage-reports/${report.id}/link-task/${taskId}`, auth);
      toast.success(t('auto.misc2.taskUnlinked'));
      onChanged?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Unlink failed'); }
  };

  return (
    <div className="md:col-span-2 border-t pt-3 mt-2" data-testid="damage-repair-tasks">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
        <Wrench className="h-3.5 w-3.5" /> {t('auto.misc2.repairTasksTracked')}
        {linked.length > 0 && (
          <span className="ml-auto text-[11px] text-gray-500 normal-case font-normal">
            Total {fmtHours(totalHours)} · {Object.entries(costByCur).map(([c, v]) => fmtMoney(v, c)).join(' · ') || '—'}
          </span>
        )}
      </div>

      {linked.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-2">{t('auto.misc2.noTasksLinkedYet')}</div>
      ) : (
        <div className="border rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-2 py-1">{t('auto.misc2.task')}</th>
                <th className="text-center px-2 py-1 w-24">{t('auto.misc2.status')}</th>
                <th className="text-right px-2 py-1 w-20">{t('auto.misc2.hours')}</th>
                <th className="text-right px-2 py-1 w-16">{t('auto.misc2.workers')}</th>
                <th className="text-right px-2 py-1 w-28">{t('auto.misc2.laborCost')}</th>
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {linked.map((tItem) => (
                <tr key={tItem.id} className="border-t" data-testid={`damage-linked-task-${tItem.id}`}>
                  <td className="px-2 py-1.5 truncate max-w-[260px]">{tItem.name || tItem.title || '—'}</td>
                  <td className="text-center px-2 py-1.5">
                    <Badge className={`${STATUS_BADGE[tItem.status] || 'bg-gray-100 text-gray-600'} text-[10px]`}>
                      {(tItem.status || '').replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="text-right px-2 py-1.5 font-mono text-xs">{fmtHours(tItem.actual_hours)}</td>
                  <td className="text-right px-2 py-1.5">{tItem.n_workers || 0}</td>
                  <td className="text-right px-2 py-1.5 font-mono text-xs">{fmtMoney(tItem.labor_cost, tItem.currency)}</td>
                  {canEdit && (
                    <td className="px-1 py-1.5">
                      <Button size="icon" variant="ghost" className="h-6 w-6"
                        onClick={() => unlink(tItem.id)} title={t('auto.misc2.unlinkTask')}
                        data-testid={`damage-unlink-task-${tItem.id}`}>
                        <X className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && linkable.length > 0 && (
        <div className="flex gap-2 mt-2">
          <Select value={picking} onValueChange={setPicking}>
            <SelectTrigger className="flex-1 h-8" data-testid="damage-link-task-picker">
              <SelectValue placeholder={t('auto.misc2.pickATaskToLink')} />
            </SelectTrigger>
            <SelectContent>
              {linkable.map((t) => {
                // Tasks store the display name under ``name`` (legacy
                // ``title`` was migrated). Fall back through both so a
                // missing/empty name never leaves the row blank.
                const label = t.name || t.title || t.template_name_at_copy || `Task ${t.id?.slice(0, 8)}`;
                return (
                  <SelectItem key={t.id} value={t.id} className="max-w-full">
                    <span className="block truncate max-w-[480px]">
                      {label}
                      {t.status ? (
                        <span className="text-slate-400"> · {t.status.replace(/_/g, ' ')}</span>
                      ) : null}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={link} disabled={!picking} data-testid="damage-link-task-btn">
            <Link2 className="h-3.5 w-3.5 mr-1" /> {t('auto.misc2.link')}
          </Button>
        </div>
      )}
      {canEdit && linkable.length === 0 && linked.length === 0 && (
        <div className="text-xs text-slate-400 italic py-1">
          {t('auto.misc2.noTasksOnThisProjectYet')}
        </div>
      )}
    </div>
  );
}
